import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AppConfig } from "../../../app.config";
import { CadastroUsuarioPlanoRequest } from "../../auth/models/CadastroUsuarioPlanoRequest.model";
import { UserLoginRequest } from "../arguments/user/user-login.request";
import { UserLoginResponse } from "../arguments/user/user-login.response";
import { ERROR_MESSAGES } from "../resources/messages";
import { Router } from "@angular/router";
import { ErrorHandlerService } from "src/app/services/errorHandler.service";
import { StorageService } from "src/app/services/storage.service";
import {
  Subject,
  Observable,
  lastValueFrom,
  map,
  catchError,
  of,
  throwError,
} from "rxjs";
import { ConfiguracoesUsuario } from "src/app/models/usuario/ConfiguracoesUsuario";

@Injectable()
export class AuthService {
  private token: string;
  private token$ = new Subject<string>();

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private storageService: StorageService
  ) {}

  get userID() {
    const json = localStorage.getItem("config");
    if (json) {
      return (<ConfiguracoesUsuario>JSON.parse(json)).id;
    }
  }

  mailLogin(request: UserLoginRequest): Promise<UserLoginResponse> {
    let response: UserLoginResponse = new UserLoginResponse();
    return lastValueFrom(
      this.httpClient.post(`${AppConfig.apiEndpoint}/auth/login`, request).pipe(
        map((result: any) => {
          response = <UserLoginResponse>result.accessToken.value;
          this.setAccessToken(response);
          return response;
        }),
        catchError((error) => {
          // Erros sem mensagem mapeada (sem internet, 500, timeout, etc.)
          // também precisam chegar na tela — sem isso o login seguia como
          // se tivesse dado certo e voltava calado para a tela de login
          response.erro = this.mensagemErro(
            error,
            ERROR_MESSAGES.ErroInesperado
          );
          this.errorHandlerService.handleError(error);
          return of(response);
        })
      )
    );
  }

  // Devolve o resultado do login automático: a senha já foi trocada neste
  // ponto, então quem chama precisa saber se o login em seguida falhou
  async alterarSenhaPrimeiroAcesso(
    novaSenha: string
  ): Promise<UserLoginResponse> {
    const result = <{ email: string }>await lastValueFrom(
      this.httpClient.post(
        `${AppConfig.apiEndpoint}/auth/alterar-senha-primeiro-acesso`,
        {
          senha: novaSenha,
        }
      )
    );
    const model = new UserLoginRequest();
    model.email = result.email;
    model.senha = novaSenha;

    return await this.mailLogin(model);
  }

  public refreshToken(email: string, key: string): Observable<any> {
    const url = `${AppConfig.apiEndpoint}/auth/refresh-token`;
    const model = {
      Email: email,
      Token: key,
    };

    return this.httpClient.post(url, model).pipe(
      map((result: any) => {
        const token = <UserLoginResponse>result.accessToken.value;
        this.setAccessToken(token);
        return token;
      }),
      catchError((error: any) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  async logoff(): Promise<void> {
    this.storageService.deletarDBs();
    localStorage.clear();
    // window.location.replace("auth/logoff");
    await this.router.navigate(["auth/logoff"]);
  }

  // Descarta só o token, sem navegar. Usado quando o login passou mas os
  // dados não puderam ser baixados: sem isso o usuário ficava "logado" sem
  // dados e caía na tela de saída ao recarregar a página
  descartarAccessToken(): void {
    localStorage.removeItem("usertoken");
    this.token = "";
    this.updateToken();
  }

  atualizaProblemaPagamento(value: UserLoginResponse) {
    value.problemaPagamento = false;
    this.setAccessToken(value);
  }

  getAccessToken(): UserLoginResponse {
    let token: UserLoginResponse = new UserLoginResponse();
    try {
      const encodedStr = localStorage.getItem("usertoken");
      const userLoginResponse = new UserLoginResponse();

      if (encodedStr) {
        const json = atob(encodedStr);
        Object.assign(userLoginResponse, JSON.parse(json));
        token = new UserLoginResponse(userLoginResponse);
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }

    return token;
  }

  userIsLoggedIn(): boolean {
    const token = this.getAccessToken();

    if (
      typeof token?.token === "string" &&
      !(token?.token.trim() === "") &&
      !(token?.token === null) &&
      !(token?.token === undefined)
    ) {
      return true;
    } else {
      return false;
    }

    // return token ? true : false;
  }

  isUserFirstAccess(): boolean {
    const token = this.getAccessToken();

    if (token?.cadastroAtivo) {
      return false;
    } else {
      return true;
    }

    // return token.cadastroAtivo ? false : true;
  }

  public async register(request: CadastroUsuarioPlanoRequest): Promise<void> {
    try {
      const planoTrial = request.tipoPlano === "trial";
      await lastValueFrom(
        this.httpClient.post(`${AppConfig.apiEndpoint}/auth/signup`, request)
      );
    } catch (error: any) {
      this.errorHandlerService.handleError(error);

      // Relança com uma mensagem para a tela — antes o erro morria aqui e
      // o cadastro seguia como se tivesse dado certo
      throw new Error(
        this.mensagemErro(error, ERROR_MESSAGES.ErroCadastro, true)
      );
    }
  }

  async solicitarTokenRedefinicaoSenha(email: string): Promise<void> {
    await lastValueFrom(
      this.httpClient.post(
        `${AppConfig.apiEndpoint}/auth/verify-email`,
        `\"${email}\"`,
        {
          headers: new HttpHeaders({
            "Content-Type": "application/json",
          }),
        }
      )
    );
  }

  async validarTokenRedefinicaoSenha(
    email: string,
    token: string
  ): Promise<void> {
    const payload = {
      email: email,
      codigo: token,
    };

    await lastValueFrom(
      this.httpClient.post(`${AppConfig.apiEndpoint}/auth/verify-code`, payload)
    );
  }

  async redefinirSenha(
    email: string,
    token: string,
    senha: string,
    confirmaSenha: string
  ): Promise<void> {
    const payload = {
      email: email,
      codigo: token,
      senha: senha,
      confirmaSenha: confirmaSenha,
    };

    await lastValueFrom(
      this.httpClient.post(
        `${AppConfig.apiEndpoint}/auth/redefine-password`,
        payload
      )
    );
  }

  updateToken(): void {
    this.token$.next(this.token);
  }

  getToken(): Observable<string> {
    return this.token$.asObservable();
  }

  // Diz ao usuário de onde vem o problema: código conhecido da API,
  // conexão dele, servidor fora, ou (padrao) algo nos dados enviados
  private mensagemErro(
    error: any,
    padrao: string,
    exibirTextoApi = false
  ): string {
    const codigo = typeof error?.error === "string" ? error?.error : "";

    if (ERROR_MESSAGES[codigo]) {
      return ERROR_MESSAGES[codigo];
    }

    // No cadastro a API já devolve o motivo escrito para o usuário
    // (ex.: "Já existe um cliente cadastrado com esse e-mail...")
    if (exibirTextoApi && codigo.trim()) {
      return codigo;
    }

    if (this.isFalhaConexao(error)) {
      return ERROR_MESSAGES.FalhaConexao;
    }

    if (error?.status >= 500) {
      return ERROR_MESSAGES.ErroServidor;
    }

    return padrao;
  }

  private isFalhaConexao(error: any): boolean {
    return (
      error?.status === 0 ||
      error?.status === 503 ||
      error?.status === 504 ||
      error?.status === 524
    );
  }

  private setAccessToken(value: UserLoginResponse): void {
    const json = JSON.stringify(value);
    localStorage.setItem("usertoken", btoa(json));
    this.token = value.token ? value.token : "";

    this.updateToken();
  }
}
